import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Label, Popover, Row } from 'reactstrap';
import Calendar from 'react-calendar';
import moment from 'moment';

import { DropdownSelectWithLabel, RadioSelection2WithLabel } from '../common/standardComponents';
import DateInput from '../input/dateInput';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import './followUpDate.css';

const SAVED_DATE_RANGE = 'savedDateRange';
const CUSTOM_DATE_RANGE = 'customDateRange';

interface FollowUpDateProps {
    customDateRangeEnd: string;
    customDateRangeEndIsValid: boolean;
    customDateRangeEndPicker: string;
    customDateRangeStart: string;
    customDateRangeStartIsValid: boolean;
    customDateRangeStartPicker: string;
    dateRangeList: any[];
    validateCustomDateRange: Function;
    onCustomDateRangeChange: (e: any) => void;
    onDatePickerChange: (e: any, type: string) => void;
    onDateRangeOptionChange: (e: any) => void;
    onDropdownChange: (e: any, option: string) => void;
    selectedDateRange: number;
    selectedDateRangeOption: string;
    shouldValidate: boolean;
}

const FollowUpDate: React.FC<FollowUpDateProps> = ({
    customDateRangeEnd,
    customDateRangeEndIsValid,
    customDateRangeEndPicker,
    customDateRangeStart,
    customDateRangeStartIsValid,
    customDateRangeStartPicker,
    dateRangeList,
    validateCustomDateRange,
    onCustomDateRangeChange,
    onDatePickerChange,
    onDateRangeOptionChange,
    onDropdownChange,
    selectedDateRange,
    selectedDateRangeOption,
    shouldValidate,
}) => {
    const [computedDateRangeText, setComputedDateRangeText] = useState<string>('');
    const [popoverOpenStart, setPopoverOpenStart] = useState<boolean>(false);
    const [popoverOpenEnd, setPopoverOpenEnd] = useState<boolean>(false);

    const toggleCalendarStart = () => setPopoverOpenStart(!popoverOpenStart);
    const toggleCalendarEnd = () => setPopoverOpenEnd(!popoverOpenEnd);

    const resolveDateRangeText = (min: number, max: number) => {
        let dateRangeText = ''
        if (min >= 0 && max > 0) {
            let startDate = moment().add(min, 'd');
            let endDate = moment().add(max, 'd');
            dateRangeText = `${startDate.format('MMMM DD, YYYY')} - ${endDate.format('MMMM DD, YYYY')}`;
        }
        return dateRangeText;
    }

    const onSavedDateRangeChange = (event: any) => {
        let textResult = '';
        let dateRangeId = parseInt(event.target.value, 10);
        if (dateRangeId > 0) {
            let dateRange = dateRangeList.find(x => x.id === dateRangeId);
            if (dateRange) {
                textResult = resolveDateRangeText(dateRange.minOffsetDays, dateRange.maxOffsetDays);
            }
        }
        setComputedDateRangeText(textResult);
        onDropdownChange(event, 'selectedDateRange');
    }

    const onOptionChange = (event: any) => {
        onDateRangeOptionChange(event);
    };

    const onStartDatePickerChange = (date: any) => {
        toggleCalendarStart();
        onDatePickerChange(date, 'customDateRangeStart');
    }

    const onEndDatePickerChange = (date: any) => {
        toggleCalendarEnd();
        onDatePickerChange(date, 'customDateRangeEnd');
    }

    const legacyToggleCalendarStart = () => {
        if (popoverOpenStart === true) {
            toggleCalendarStart();
        }
    }

    const legacyToggleCalendarEnd = () => {
        if (popoverOpenEnd === true) {
            toggleCalendarEnd();
        }
    }
    return (
        <React.Fragment>
            <Row>
                <Col>
                    <RadioSelection2WithLabel
                        id="selectedDateRangeOption"
                        label="Date Range"
                        value={selectedDateRangeOption}
                        onChange={onOptionChange}
                        option1Label="Select Date Range"
                        option1Value={SAVED_DATE_RANGE}
                        option2Label="Input Custom Date Range"
                        option2Value={CUSTOM_DATE_RANGE} />
                </Col>
            </Row>
            <Row className="custom-date-range-row">
                {selectedDateRangeOption === SAVED_DATE_RANGE &&
                    <React.Fragment>
                        <Col>
                            <div className="d-flex flex-row align-items-center">
                                <DropdownSelectWithLabel
                                    id="dateRange"
                                    isRequired
                                    label="Follow Up Date Range"
                                    onChange={(e:any) => onSavedDateRangeChange(e)}
                                    optionList={dateRangeList}
                                    shouldValidate={shouldValidate}
                                    value={selectedDateRange} />
                                <div className="p-2">
                                    <p className='date-range-text'>{computedDateRangeText}</p>
                                </div>
                            </div>
                        </Col>
                    </React.Fragment>
                }
                {selectedDateRangeOption === CUSTOM_DATE_RANGE &&
                    <React.Fragment>
                        <Col>
                            <Row>
                                <Col>
                                    <Label className="fw-bold">CUSTOM DATE RANGE</Label>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <div className="d-inline-flex">
                                        <div className="txtbx-date-range">
                                            <DateInput
                                                type="text"
                                                name="customDateRangeStart"
                                                id="customDateRangeStart"
                                                onChange={onCustomDateRangeChange}
                                                validate={validateCustomDateRange}
                                                value={customDateRangeStart}
                                                className={"form-control " + (customDateRangeStartIsValid ? undefined : 'is-invalid')}
                                                placeholder="Start Date"
                                                isRequired
                                            />
                                            <div className="invalid-feedback">
                                                Please provide a current or future start date.
                                            </div>
                                        </div>
                                        <div className="p-2">
                                            <FontAwesomeIcon className="hand calendar-icon" id="popoverOpenStart" icon={faCalendarAlt} size="lg" onClick={toggleCalendarStart} />
                                            <Popover placement="right" isOpen={popoverOpenStart} target="popoverOpenStart" toggle={legacyToggleCalendarStart} trigger="legacy">
                                                <Calendar
                                                    name="customDateRangeStartPicker"
                                                    id="customDateRangeStartPicker"
                                                    calendarType="US"
                                                    formatMonthYear={(locale:any, value:any) => { return moment(value).format('MMMM[\r\n]YYYY'); }}
                                                    className="pop-calendar"
                                                    onChange={onStartDatePickerChange}
                                                    value={customDateRangeStartPicker} />
                                            </Popover>
                                        </div>
                                        <div className="txt-date-range">
                                            <DateInput
                                                type="text"
                                                name="customDateRangeEnd"
                                                id="customDateRangeEnd"
                                                onChange={onCustomDateRangeChange}
                                                validate={validateCustomDateRange}
                                                value={customDateRangeEnd}
                                                className={"form-control " + (customDateRangeEndIsValid ? undefined : 'is-invalid')}
                                                placeholder="End Date"
                                                isRequired
                                            />
                                            <div className="invalid-feedback">
                                                Please provide a future end date after the start date.
                                            </div>
                                        </div>
                                        <div className="p-2">
                                            <React.Fragment>
                                                <FontAwesomeIcon className="hand calendar-icon" id="popoverOpenEnd" icon={faCalendarAlt} size="lg" onClick={toggleCalendarEnd} />
                                                <Popover placement="right" isOpen={popoverOpenEnd} target="popoverOpenEnd" toggle={legacyToggleCalendarEnd} trigger="legacy">
                                                    <Calendar
                                                        name="customDateRangeEndPicker"
                                                        id="customDateRangeEndPicker"
                                                        calendarType="US"
                                                        formatMonthYear={(locale:any, value:any) => { return moment(value).format('MMMM[\r\n]YYYY'); }}
                                                        className="pop-calendar"
                                                        onChange={onEndDatePickerChange}
                                                        value={customDateRangeEndPicker} />
                                                </Popover>
                                            </React.Fragment>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </React.Fragment>
                }
            </Row>
        </React.Fragment>
    );
}
export default FollowUpDate;